import React, { useCallback, useEffect, useMemo, useState } from "react";
import type { Key, Dispatch, SetStateAction } from "react";
import { Checkbox, Form, Modal, ModalProps, Select, Tooltip, Tree } from "antd";
import type { DataNode } from "antd/es/tree";
import { uniqueId, cloneDeep } from "lodash";
import { IOfferListSelectMode } from "../../../containers/OfferContainer/context/types";
import { ReactComponent as PlusOneIcon } from "../../../img/plus-one-icon.svg";
import { ReactComponent as PlusIcon } from "../../../img/plus.svg";
import { ReactComponent as CancelIcon } from "../../../img/cancel.svg";
import type { Offer } from "../../../lib/types";
import type { IOfferHierarchy } from "../../../store/slices/offering/types";
import { useActiveModal } from "../../../lib/utils";
import { ITreeDataItemOnChange } from "./ProductParamsTreeTypes";
import { formatKey } from "../utils/formatKey";
import "./ProductParamsTree.less";

interface ComponentProps {
  treeExpandedKeys: string[];
  setTreeExpandedKeys: Dispatch<SetStateAction<string[]>>;
  offer: Offer<string> | undefined;
  offerHierarchy: IOfferHierarchy | undefined;
  treeDataFormat: DataNode[];
  setTreeDataFormat: React.Dispatch<React.SetStateAction<DataNode[]>>;
  offerListSelectMode: IOfferListSelectMode;
}

/* ------------------------------------------------------ Начало компонента ------------------------------------------------------ */

export const ProductParamsTree = (props: ComponentProps) => {
  const {
    treeExpandedKeys,
    setTreeExpandedKeys,
    offer,
    offerHierarchy,
    treeDataFormat,
    setTreeDataFormat,
    offerListSelectMode,
  } = props;

  // Создано ли начальное состояние всех параметров древа
  const [isAcceptTreeInitial, setIsAcceptTreeInitial] = useState(false);

  // Начальное состояние всех параметров древа
  const [keyVisibleTreeInitial, setKeyVisibleTreeInitial] = useState<{
    [key: string]: {
      value: number;
      minCardinality?: number;
      maxCardinality?: number;
      defaultCardinality?: number;
      maxElements: number;
      visible?: number;
    };
  }>({});

  // Изменяемое состояние всех параметров древа для добавления новых строк или удаления из древа
  const [keyVisibleTreeFormat, setKeyVisibleTreeFormat] = useState<{
    [key: string]: {
      value: number;
      minCardinality?: number;
      maxCardinality?: number;
      defaultCardinality?: number;
      maxElements: number;
      visible?: number;
    };
  }>({});

  // Функция для создания начального состояния всех параметров древа
  const getKeyVisibleTreeInitial = (
    hierarchy: Partial<IOfferHierarchy>,
    parentKey?: string
  ): DataNode & {
    minCardinality?: number;
    maxCardinality?: number;
    defaultCardinality?: number;
    id?: number;
  } => {
    const { id, children, minCardinality, maxCardinality, defaultCardinality } = hierarchy;

    const key = [parentKey, id].filter(Boolean).join("-");

    // Создание начального состояния всех параметров древа
    if (!isAcceptTreeInitial) {
      setKeyVisibleTreeInitial((prev) => {
        if (prev[key]) {
          return {
            ...prev,
            [key]: {
              ...prev[key],
              value: prev[key]?.value + 1,
            },
          };
        }
        const maxElements = children?.reduce((sum, el) => sum + (el.maxCardinality || 0), 0) || 0;

        return {
          ...prev,
          [key]: {
            value: 1,
            visible: defaultCardinality,
            minCardinality,
            maxCardinality,
            defaultCardinality,
            maxElements,
          },
        };
      });
    }

    return {
      key,
      children: children
        ? children.map((child) => getKeyVisibleTreeInitial(child, key))
        : undefined,
    };
  };

  // Старт создания начального состояния всех параметров древа
  useEffect(() => {
    if (offerHierarchy) {
      getKeyVisibleTreeInitial({
        ...offerHierarchy,
        children: offerHierarchy.children,
      });
      setIsAcceptTreeInitial(true);
    }
  }, [offerHierarchy]);

  // После создания начального состояния всех параметров древа добавляем их в состояние для будущего изменения
  useEffect(() => {
    if (isAcceptTreeInitial) {
      setKeyVisibleTreeFormat(keyVisibleTreeInitial);
    }
  }, [isAcceptTreeInitial]);

  const getTreeDataItem = (
    hierarchy: Partial<IOfferHierarchy & { identificator: string }>,
    parentKey?: string,
    index?: string
  ): DataNode & {
    minCardinality?: number;
    maxCardinality?: number;
    defaultCardinality?: number;
    id?: number;
    keyOnRowClicked?: string;
  } => {
    const {
      extName,
      id,
      children,
      minCardinality,
      maxCardinality,
      defaultCardinality,
      keyOnRowClicked,
    } = hierarchy;

    const key = [parentKey, id, index].filter(Boolean).join("-");

    // Форматирование дерева под изначальные параметры
    const childrenFormat: Partial<IOfferHierarchy<string>>[] | undefined = isAcceptTreeInitial
      ? children?.flatMap((child) => Array(child.defaultCardinality).fill(child))
      : [];

    return {
      ...hierarchy,
      title: extName,
      key,
      keyOnRowClicked,
      selectable: false,
      children: children
        ? childrenFormat?.map((child) =>
            getTreeDataItem({ ...child, keyOnRowClicked }, key, `el${uniqueId()}`)
          )
        : undefined,
      className: "product-params-tree__row",
      minCardinality,
      maxCardinality,
      defaultCardinality,
      id,
    };
  };

  const treeData = useMemo((): DataNode[] => {
    if (offerHierarchy && isAcceptTreeInitial) {
      return [
        getTreeDataItem({
          ...offerHierarchy,
          children: offerHierarchy.children,
        }),
      ];
    }
    if (offer) {
      return [
        getTreeDataItem({
          extName: offer.extName,
          id: offer.id,
          children: undefined,
        }),
      ];
    }
    setIsAcceptTreeInitial(false);
    return [];
  }, [offerHierarchy, offer, isAcceptTreeInitial]);

  const [keyUniqueForUpdateComponentTree, setKeyUniqueForUpdateComponentTree] = useState<string>(
    uniqueId()
  );

  // Добавляем дерево в стейт для дальнейшего редактирования
  useEffect(() => {
    setTreeDataFormat(treeData);
    setKeyUniqueForUpdateComponentTree(uniqueId());
  }, [treeData]);

  // Дефолтное открытие всех элементов дерева
  useEffect(() => {
    const allKeys: string[] = [];
    const addKey = ({ key, children }: DataNode) => {
      if (children && children.length > 0) {
        allKeys.push(String(key));
        children.forEach(addKey);
      }
    };
    treeData.forEach(addKey);
    setTreeExpandedKeys(allKeys);
  }, [treeData]);

  const getTreeDataItemOnChange = (
    tree: ITreeDataItemOnChange,
    parentKey?: string | number,
    index?: string,
    mode?: "add" | "delete" | "create",
    keyFind?: string,
    newGenerated?: boolean,
    newElement?: IOfferHierarchy<string>
  ): ITreeDataItemOnChange => {
    const { id, key, children } = tree;

    const childrenFormat: DataNode[] = [];

    const findIndex = children?.findIndex((el) => el.key === keyFind);
    let newKeyForNewChild = "";

    if (mode === "delete" && children) {
      if (findIndex !== undefined && findIndex !== -1) {
        children.splice(findIndex, 1);
        childrenFormat.push(...children);
      } else {
        childrenFormat.push(...children);
      }
    }

    if (mode === "add" && children) {
      if (findIndex !== undefined && findIndex !== -1) {
        const childrenCopy = cloneDeep(children);

        const keyClear = String(children[findIndex].key).split("-").slice(0, -1);

        keyClear.push(`el${uniqueId()}`);

        newKeyForNewChild = keyClear.join("-");

        // --- Start ---
        // Для глубокого добавления keyOnRowClicked(нужен чтобы useProductTableData корректно копировал данные)
        const addKeyOnRowClickedRecursively = (node, keyOnRowClicked?: string) => {
          const newNode = { ...node, keyOnRowClicked };

          if (Array.isArray(newNode.children)) {
            newNode.children = newNode.children.map((child) =>
              addKeyOnRowClickedRecursively(child, child.key)
            );
          }

          return newNode;
        };
        const clonedWithKey = addKeyOnRowClickedRecursively(cloneDeep(childrenCopy[findIndex]));
        // --- End ---

        childrenCopy.splice(findIndex, 0, {
          ...clonedWithKey,
          key: newKeyForNewChild,
          keyOnRowClicked: keyFind,
        });

        childrenFormat.push(...childrenCopy);
      } else {
        childrenFormat.push(...children);
      }
    }

    if (mode === "create" && children) {
      if (key === keyFind) {
        const childrenCopy = cloneDeep(children);

        const newKey = [keyFind, newElement?.id, `el${uniqueId()}`].filter(Boolean).join("-");

        childrenCopy.push({
          ...newElement,
          title: newElement?.extName,
          key: newKey,
          keyOnRowClicked: keyFind,
          selectable: false,
          children: children
            ? newElement?.children.map((child) => {
                return {
                  ...getTreeDataItem(
                    { ...child, keyOnRowClicked: keyFind },
                    newKey,
                    `el${uniqueId()}`
                  ),
                };
              })
            : undefined,
          className: "product-params-tree__row",
          minCardinality: newElement?.minCardinality,
          maxCardinality: newElement?.maxCardinality,
          defaultCardinality: newElement?.defaultCardinality,
          id: newElement?.id,
        });

        childrenFormat.push(...childrenCopy);
      } else {
        childrenFormat.push(...children);
      }
    }

    const newKey = [parentKey, id, index, newGenerated ? `el${uniqueId()}` : ""]
      .filter(Boolean)
      .join("-");

    return {
      ...tree,

      key: index || newGenerated ? newKey : key,

      children: children
        ? childrenFormat.map((child) => {
            if (child.key === newKeyForNewChild) {
              return getTreeDataItemOnChange(
                child,
                key,
                `el${uniqueId()}`,
                mode,
                keyFind,
                false,
                newElement
              );
            }
            return getTreeDataItemOnChange(
              child,
              index || newGenerated ? newKey : key,
              undefined,
              mode,
              keyFind,
              !!(index || newGenerated),
              newElement
            );
          })
        : undefined,
    };
  };

  const onChangeTreeDataFormat = (
    keyFind,
    mode: "add" | "delete" | "create",
    newElement?: IOfferHierarchy<string>
  ) => {
    setTreeDataFormat((prev) => {
      return [
        getTreeDataItemOnChange(
          {
            ...prev[0],
            children: prev[0].children,
          },
          undefined,
          undefined,
          mode,
          keyFind,
          false,
          newElement
        ),
      ];
    });
  };

  // Вообще все элементы которые есть в иерархии раздельно
  const parseTree = useMemo(() => {
    const parseTreeMemo: { [key: string]: ITreeDataItemOnChange[] } = {};

    const treeDataParse = (tree) => {
      if (tree?.children?.length === 0 || !tree?.children) {
        parseTreeMemo[tree?.key] = [];
        return;
      }
      parseTreeMemo[tree.key] = tree.children;
      tree.children.forEach((el) => treeDataParse(el));
    };

    treeDataParse(treeDataFormat[0]);

    return parseTreeMemo;
  }, [treeDataFormat[0]]);

  // Вообще все элементы которые есть в иерархии изначальной
  const parseHierarchyAllElements = useMemo(() => {
    const parseHierarchyAllElementsMemo: IOfferHierarchy[] = [];

    const getParseHierarchyAllElements = (tree) => {
      if (tree?.children?.length === 0 || !tree?.children) {
        parseHierarchyAllElementsMemo.push(tree);
      } else {
        parseHierarchyAllElementsMemo.push(tree);
        tree.children.forEach((el) => getParseHierarchyAllElements(el));
      }
    };

    getParseHierarchyAllElements(offerHierarchy);

    return parseHierarchyAllElementsMemo;
  }, [offerHierarchy, offer]);

  // Получение уже отображаемых ключей для предложения
  const getKeyVisible = (findKey: string | number) => {
    const keyVisible: string[] = [];

    Object.entries(parseTree).forEach((el) => {
      if (String(el[0]) === findKey) {
        if (Array.isArray(el[1])) {
          keyVisible.push(
            ...new Set(
              el[1].map((elKey) => {
                return String(String(elKey.key).split("-").slice(-2)[0]);
              })
            )
          );
        }
      }
    });

    return keyVisible;
  };

  // Получение скрытых ключей для предложения
  const getKeyInvisible = (findKey: string | number, keyVisible: string[]) => {
    const keyInvisible: { key: string; value: {} }[] = [];

    Object.entries(keyVisibleTreeInitial).forEach((el) => {
      const findElementOfTree = String(el[0]).indexOf(formatKey(findKey));
      if (
        findElementOfTree !== -1 &&
        formatKey(findKey).split("-").length + 1 === String(el[0]).split("-").length
      ) {
        const idElement = el[0].split("-").slice(-1)[0];

        const findInVisible = keyVisible.find((keyVis) => keyVis === idElement);

        if (!findInVisible) {
          keyInvisible.push({
            key: el[0],
            value: el[1],
          });
        }
      }
    });
    return keyInvisible;
  };

  // Отображения title для каждого элемента дерева
  const titleRender = (row: ITreeDataItemOnChange) => {
    const { title, key } = row;

    const formattingKey = formatKey(key);

    const isRoot = String(formattingKey).split("-").length === 1;

    let findSumVisible = 0;

    Object.entries(parseTree).forEach((el) => {
      if (
        String(el[0]).split("-").slice(0, -1).join("-") ===
        String(row.key).split("-").slice(0, -1).join("-")
      ) {
        findSumVisible += 1;
      }
    });

    const plusIcon = () => {
      const keyVisible = getKeyVisible(key);

      let keyInvisible: {
        key: string;
        value: {};
      }[] = [];

      if (offerListSelectMode === "basedOn") {
        keyInvisible = getKeyInvisible(key, keyVisible);
      } else {
        keyInvisible = keyVisible.length > 0 ? getKeyInvisible(key, keyVisible) : [];
      }

      if (keyInvisible.length !== 0) {
        return (
          <PlusIcon
            width={16}
            height={16}
            className="product-params-tree__addBtn"
            onClick={() => {
              openAddOfferProductsParamsModal(key);
            }}
          />
        );
      }
    };

    const plusOneIcon = () => {
      const onPluseOneIcon = () => {
        setKeyVisibleTreeFormat((prev) => {
          return {
            ...prev,
            [formattingKey]: {
              ...prev[formattingKey],
              visible: Number(prev[formattingKey]?.visible) + 1,
            },
          };
        });
        onChangeTreeDataFormat(key, "add");
      };

      // Если создать на основании, то можем максимум 10 одинаковых добавить
      if (
        !isRoot &&
        findSumVisible < (offerListSelectMode === "basedOn" ? 10 : Number(row?.maxCardinality))
      ) {
        return (
          <PlusOneIcon
            width={16}
            height={16}
            className="product-params-tree__addBtn"
            onClick={onPluseOneIcon}
          />
        );
      }
    };

    const cancelIcon = () => {
      const onCancelIcon = () => {
        setKeyVisibleTreeFormat((prev) => {
          return {
            ...prev,
            [formattingKey]: {
              ...prev[formattingKey],
              visible: Number(prev[formattingKey]?.visible) - 1,
            },
          };
        });
        onChangeTreeDataFormat(key, "delete");
      };

      // Если создать на основании, то можем удалять в 0, даже обязательные
      if (
        !isRoot &&
        Number(findSumVisible) >
          (offerListSelectMode !== "basedOn"
            ? Number(keyVisibleTreeFormat[formattingKey]?.minCardinality)
            : 0)
      ) {
        return (
          <CancelIcon
            className="product-params-tree__addBtn"
            style={{ zIndex: 1 }}
            onClick={onCancelIcon}
          />
        );
      }
    };

    return (
      <div className="tree-title-container">
        <div>
          <Tooltip title={title}>
            <h4 className="tree-title">{title}</h4>
          </Tooltip>
        </div>
        <div>
          {plusIcon()}
          {plusOneIcon()}
          {cancelIcon()}
        </div>
      </div>
    );
  };

  // По дефолту в keys остаются активными вложенные элементы свернутого узла, удаление таковых
  const onExpand = (keys: Key[], { expanded, node }) => {
    if (expanded) {
      setTreeExpandedKeys(keys.map(String));
    } else {
      setTreeExpandedKeys((prevKeys) => {
        const activeKeys = prevKeys.filter((key) => !String(key).startsWith(String(node.key)));
        return activeKeys;
      });
    }
  };

  // Отображение линий визуальных у дерева
  const showLine = useMemo(() => {
    if (offerHierarchy?.children && offerHierarchy?.children.length > 0) {
      return { showLeafIcon: false };
    }
    return false;
  }, [offerHierarchy]);

  const {
    activeModal: activeModalAddOfferProductParams,
    setActiveModal: setActiveModalAddOfferProductParams,
  } = useActiveModal<"" | "createGroupOfferProductsParams">("");

  const [selectedAddOfferProductsParams, setSelectedAddOfferProductsParams] = useState<
    IOfferHierarchy<string>
  >();

  const [findAddKey, setFindAddKey] = useState("");

  const openAddOfferProductsParamsModal = useCallback((keyFind) => {
    setFindAddKey(keyFind);
    setActiveModalAddOfferProductParams("createGroupOfferProductsParams");
  }, []);

  const closeAddOfferProductsParamsModal = useCallback(() => {
    setActiveModalAddOfferProductParams("");
    setSelectedAddOfferProductsParams(undefined);
  }, []);

  const submitAddOfferProductsParamsModal = useCallback(
    (selectedOffer: IOfferHierarchy<string>) => {
      onChangeTreeDataFormat(findAddKey, "create", selectedOffer);

      const keyAdd = [formatKey(findAddKey), selectedOffer.id].join("-");

      setKeyVisibleTreeFormat((prev) => {
        return {
          ...prev,
          [keyAdd]: {
            ...prev[keyAdd],
            visible: 1,
          },
        };
      });

      setActiveModalAddOfferProductParams("");
      setSelectedAddOfferProductsParams(undefined);
    },
    [findAddKey]
  );

  const addOfferProductParamsModalProps: ModalProps = useMemo(
    () => ({
      title: <h3 style={{ color: "#58647C" }}>Добавить предложение</h3>,
      visible: activeModalAddOfferProductParams === "createGroupOfferProductsParams",
      okButtonProps: {
        style: {
          width: "108px",
        },
        onClick: () => {
          if (selectedAddOfferProductsParams) {
            submitAddOfferProductsParamsModal(selectedAddOfferProductsParams);
          }
        },
        disabled: !selectedAddOfferProductsParams,
      },
      cancelButtonProps: {
        style: {
          width: "108px",
        },
        onClick: closeAddOfferProductsParamsModal,
      },
      onCancel: closeAddOfferProductsParamsModal,
    }),
    [activeModalAddOfferProductParams, selectedAddOfferProductsParams, offerHierarchy]
  );

  const addedOfferModal = () => {
    const keyVisible = getKeyVisible(findAddKey);

    const keyInvisible = getKeyInvisible(findAddKey, keyVisible);

    const arrOptions = keyInvisible.map((el) => {
      const keyParent = el.key.split("-").slice(-2)[0];
      const keySearcElement = el.key.split("-").slice(-2)[1];
      const findParent = parseHierarchyAllElements.find(
        (elHierarchy) => String(elHierarchy.id) === String(keyParent)
      );
      const findChildSearchElement = findParent?.children.find(
        (elChild) => String(elChild.id) === String(keySearcElement)
      );

      return findChildSearchElement;
    });

    return (
      <>
        {addOfferProductParamsModalProps.visible && (
          <Modal {...addOfferProductParamsModalProps}>
            <Form layout="horizontal" initialValues={{ visibleAll: false }}>
              <Form.Item dependencies={["visibleAll"]} noStyle>
                {({ getFieldValue }) => {
                  const visibleAll = getFieldValue("visibleAll");

                  return (
                    <Form.Item
                      name="offersId"
                      label={<span style={{ color: "#778899" }}>Предложение</span>}
                      labelCol={{ span: 6, flex: "start" }}
                      rules={[
                        {
                          required: true,
                        },
                      ]}
                      className="form-item-required__hidden-error product_params_tree_form_item"
                    >
                      <Select
                        allowClear
                        placeholder="Выберите предложение"
                        onChange={(e) => {
                          const [id, ogLinkId] = String(e).split("-");
                          setSelectedAddOfferProductsParams(
                            parseHierarchyAllElements.find(
                              (el) =>
                                Number(el.id) === Number(id) &&
                                Number(el.ogLinkId) === Number(ogLinkId)
                            )
                          );
                        }}
                      >
                        {arrOptions
                          ?.filter((el) => (visibleAll ? el : el?.offeringStatusId === 2))
                          .map((el) => (
                            <Select.Option key={el?.id} value={String(`${el?.id}-${el?.ogLinkId}`)}>
                              {el?.name} / {el?.extName}
                            </Select.Option>
                          ))}
                      </Select>
                    </Form.Item>
                  );
                }}
              </Form.Item>
              <Form.Item
                name="visibleAll"
                label={<span style={{ color: "#778899" }}>Показывать все</span>}
                valuePropName="checked"
                labelCol={{ span: 6 }}
                className="form-item-required__hidden-error product_params_tree_form_item"
              >
                <Checkbox />
              </Form.Item>
            </Form>
          </Modal>
        )}
      </>
    );
  };

  return (
    <div className="product-params-tree">
      <div className="product-params-tree__title">Наименование</div>
      <Tree
        showLine={showLine}
        treeData={treeDataFormat}
        showIcon={false}
        onExpand={onExpand}
        expandedKeys={treeExpandedKeys}
        titleRender={titleRender}
        key={keyUniqueForUpdateComponentTree}
      />
      {/* Модалка для + (добавление нового предложения на этапе параметров продукта в иерархию) */}
      {addedOfferModal()}
    </div>
  );
};
